import { createElement, useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, Loader2, PlugZap, RefreshCw, ShieldAlert, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AUTHORITY_LABELS, AUTHORITY_OPTIONS, CAPABILITY_LABELS, CONNECTION_STATES, EDITABLE_FIELDS, FIELD_LABELS, HUB_GET_CONFIG, HUB_PATH,
  IMPACT_PATH, IMPACT_POST_CONFIG, PENDING_LABELS, PROVIDER_LABELS, PROVIDER_OPTIONS, RISK_TEXT, UNKNOWN_CURRENT, buildChanges, classifyHubError,
  currentValue, describeCode, describeImpactError, formatSyncTime, normalizeHub, normalizeImpact, ownerLabel,
} from "@/lib/integrationHub";

// Entegrasyon Merkezi — YALNIZ salt-okunur görünüm ve dry-run önizleme.  Kaydet/uygula/bağlantıyı kes/sahiplik yazma YOKTUR.
// GET /integration-hub 404 (özellik kapalı) veya 401/403 verirse bileşen HİÇBİR ŞEY çizmez; mevcut Firmalarım ekranı aynen kalır.
// Sunucu yanıtı state'e yalnız `normalizeHub`/`normalizeImpact` allowlist'inden geçerek girer; ham gövde/kimlik/token saklanmaz.

const SKELETON_DELAY_MS = 400;

const TONE_CLASS = {
  ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
  warn: "bg-amber-50 text-amber-900 border-amber-200",
  danger: "bg-red-50 text-red-800 border-red-200",
  muted: "bg-muted/60 text-foreground/80 border-border",
};
const TONE_ICON = { ok: CheckCircle2, warn: AlertTriangle, danger: XCircle, muted: Info };

function ToneBadge({ tone, children }) {
  const Icon = TONE_ICON[tone] || Info;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${TONE_CLASS[tone] || TONE_CLASS.muted}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {children}
    </span>
  );
}

// Yerel <select>: dev-sunucusundaki görsel-düzenleme Babel eklentisi JSX ifade kapsayıcılarını <span> ile sarar; <select> içinde <span>
// geçersizdir (tarayıcı seçenekleri düşürür).  createElement çocukları JSX olmadığından sarılmaz → hem dev hem üretimde güvenli.
function NativeSelect({ id, value, disabled, onChange, options, labels, emptyLabel, emptyDisabled }) {
  const items = [];
  if (emptyLabel) items.push(createElement("option", { key: "__empty", value: "", disabled: Boolean(emptyDisabled) }, emptyLabel));
  for (const o of options) items.push(createElement("option", { key: o, value: o }, labels[o]));
  return createElement("select", {
    id, value, disabled, onChange: (e) => onChange(e.target.value),
    className: "mt-1 h-10 w-full rounded-xl border border-border bg-card px-3 text-sm",
  }, ...items);
}

function CodeList({ codes, testid }) {
  if (!codes?.length) return null;
  return (
    <ul className="mt-2 space-y-1 text-xs text-muted-foreground" data-testid={testid}>
      {codes.map((c, i) => <li key={`${c}-${i}`} className="flex gap-1.5"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" /><span>{describeCode(c)}</span></li>)}
    </ul>
  );
}

function ProviderCard({ p }) {
  const state = CONNECTION_STATES[p.connectionState];
  return (
    <li className="rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid={`hub-card-${p.provider || "unknown"}`}>
      <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1.5">
        <div className="flex items-center gap-2">
          <PlugZap className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <h4 className="text-sm font-semibold text-slate-800">{p.label}</h4>
        </div>
        <ToneBadge tone={state.tone}>{state.label}</ToneBadge>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{state.hint}</p>
      <dl className="mt-3 space-y-1.5 text-xs">
        <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Kimlik bilgileri</dt><dd className="font-medium">{p.hasCredentials ? "Tanımlı" : "Eksik"}</dd></div>
        <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Son başarılı senkron</dt><dd className="font-medium" data-testid="hub-last-success">{formatSyncTime(p.lastSuccess)}</dd></div>
        <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Son başarısız senkron</dt><dd className="font-medium" data-testid="hub-last-failure">{formatSyncTime(p.lastFailure)}</dd></div>
      </dl>
      {p.capabilities.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-slate-700">Sağlayıcının desteklediği işlemler</p>
          <p className="text-[11px] text-muted-foreground">Bu liste teknik desteği gösterir; hesabınızdaki yetki anlamına gelmez.</p>
          <ul className="mt-1.5 flex flex-wrap gap-1">
            {p.capabilities.map((c) => <li key={c}><Badge variant="outline" className="rounded-md text-[11px] font-normal">{CAPABILITY_LABELS[c]}</Badge></li>)}
          </ul>
        </div>
      )}
      {p.scopesReported.length > 0 && (
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid="hub-scopes">
          {p.scopesVerified ? "Doğrulanmış kapsamlar" : "Bildirilen kapsamlar (doğrulanmadı, yetki olarak kabul edilmez)"}: {p.scopesReported.join(", ")}
        </p>
      )}
      <CodeList codes={p.issues} testid="hub-provider-issues" />
    </li>
  );
}

function OwnershipMatrix({ ownership }) {
  if (!ownership) return null;
  const rows = ["incoming_source", "invoice_provider", "outgoing_source", "inventory_owner", "accounting_owner", "customer_ledger_owner", "cash_bank_owner", "settlement_owner", "product_catalog_owner"];
  const value = (f) => {
    if (f === "incoming_source") return ownership.incomingSource ? PROVIDER_LABELS[ownership.incomingSource] || "Bilinmeyen sağlayıcı" : "Seçilmedi";
    if (f === "invoice_provider") return ownership.invoiceProvider ? PROVIDER_LABELS[ownership.invoiceProvider] || "Bilinmeyen sağlayıcı" : "Seçilmedi";
    return ownerLabel(ownership.fields[f]);
  };
  return (
    <section aria-labelledby="hub-ownership-title" className="mt-6">
      <h3 id="hub-ownership-title" className="text-sm font-semibold text-slate-800">Sahiplik Matrisi</h3>
      <p className="text-xs text-muted-foreground">Hangi verinin hangi sistemde yönetildiği. Bu görünüm salt-okunurdur.</p>
      <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3" data-testid="hub-ownership">
        {rows.map((f) => {
          const o = ownership.fields[f];
          const derived = o && o.explicit === false && f !== "incoming_source" && f !== "invoice_provider";
          return (
            <div key={f} className="rounded-xl border border-border bg-card px-3 py-2.5" data-testid={`hub-owner-${f}`}>
              <dt className="text-xs text-muted-foreground">{FIELD_LABELS[f]}</dt>
              <dd className="mt-0.5 text-sm font-medium text-slate-800">{value(f)}{derived && <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">(türetilmiş)</span>}</dd>
            </div>
          );
        })}
      </dl>
      <CodeList codes={ownership.issues} testid="hub-ownership-issues" />
    </section>
  );
}

function Finding({ f }) {
  const tone = f.severity === "blocker" ? "danger" : f.severity === "warning" ? "warn" : "muted";
  const label = f.severity === "blocker" ? "Engel" : f.severity === "warning" ? "Uyarı" : "Bilgi";
  return (
    <li className="flex flex-wrap items-start gap-2 text-sm" data-severity={f.severity}>
      <ToneBadge tone={tone}>{label}</ToneBadge>
      <span className="min-w-0 flex-1 break-words">{describeCode(f.code)}{f.field ? <span className="text-muted-foreground"> — {FIELD_LABELS[f.field]}</span> : null}</span>
    </li>
  );
}

function ImpactResult({ result }) {
  const risk = RISK_TEXT[result.riskLevel] || RISK_TEXT.BLOCKING;
  const bySeverity = (s) => result.findings.filter((f) => f.severity === s);
  return (
    <div className="mt-4 space-y-4" data-testid="hub-impact-result" aria-live="polite">
      <div className="flex flex-wrap items-center gap-2">
        <ToneBadge tone={risk.tone}>{risk.label}</ToneBadge>
        <ToneBadge tone={result.canApply ? "ok" : "danger"}>{result.canApply ? "Engel bulunmadı" : "Engeller var"}</ToneBadge>
        <span className="text-xs text-muted-foreground">Bu bir önizlemedir; hiçbir ayar değiştirilmedi ve uygulanamaz.</span>
      </div>
      <section aria-labelledby="hub-impact-changes">
        <h4 id="hub-impact-changes" className="text-sm font-semibold">Değişen sahipler</h4>
        {result.changes.length === 0 ? <p className="text-sm text-muted-foreground">Değişiklik bulunmadı.</p> : (
          <ul className="mt-2 space-y-2">
            {result.changes.map((c) => (
              <li key={c.field} className="rounded-xl border border-border bg-card px-3 py-2.5" data-testid={`hub-change-${c.field}`}>
                <p className="text-xs text-muted-foreground">{FIELD_LABELS[c.field]}</p>
                <p className="mt-0.5 text-sm font-medium"><span>{ownerLabel(c.before)}</span><span aria-hidden="true"> → </span><span className="sr-only"> değişiyor: </span><span>{ownerLabel(c.after)}</span></p>
                <CodeList codes={c.impacts} />
              </li>
            ))}
          </ul>
        )}
      </section>
      <section aria-labelledby="hub-impact-pending">
        <h4 id="hub-impact-pending" className="text-sm font-semibold">Bekleyen işler</h4>
        <dl className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2" data-testid="hub-impact-pending">
          {Object.entries(result.pending).filter(([k]) => PENDING_LABELS[k]).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-2 rounded-lg bg-muted/40 px-3 py-1.5 text-sm">
              <dt className="min-w-0 break-words text-muted-foreground">{PENDING_LABELS[k]}</dt>
              <dd className="shrink-0 font-medium">{v.count === null ? "Sayılamadı" : `${v.count}${v.capped ? "+" : ""}`}</dd>
            </div>
          ))}
        </dl>
      </section>
      {["blocker", "warning", "info"].map((sev) => bySeverity(sev).length > 0 && (
        <section key={sev} aria-label={sev === "blocker" ? "Engeller" : sev === "warning" ? "Uyarılar" : "Bilgiler"} data-testid={`hub-findings-${sev}`}>
          <h4 className="text-sm font-semibold">{sev === "blocker" ? "Engeller" : sev === "warning" ? "Uyarılar" : "Bilgiler"}</h4>
          <ul className="mt-2 space-y-1.5">{bySeverity(sev).map((f, i) => <Finding key={`${f.code}-${f.field}-${i}`} f={f} />)}</ul>
        </section>
      ))}
    </div>
  );
}

function DryRunSection({ ownership }) {
  const [selections, setSelections] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const changes = buildChanges(ownership, selections) || {};
  const hasChange = Object.keys(changes).length > 0;
  const canRun = hasChange && !running;

  const onSelect = (field, value) => {
    setSelections((s) => ({ ...s, [field]: value }));
    setResult(null);       // eski sonuç yeni seçimle karışmasın; İSTEK ATILMAZ (yalnız açık düğme tıklaması çağırır)
    setError("");
  };

  const analyze = async () => {
    if (inFlight.current) return;
    const body = buildChanges(ownership, selections);
    if (!body || Object.keys(body).length === 0) return;
    inFlight.current = true;
    setRunning(true); setError(""); setResult(null);
    try {
      const data = await api.post(IMPACT_PATH, { changes: body }, IMPACT_POST_CONFIG);   // yalnız değişen alanlar; tam yapılandırma YOK
      const normalized = normalizeImpact(data);
      if (!mounted.current) return;
      if (normalized) setResult(normalized); else setError(describeImpactError({ status: 503 }));
    } catch (err) {
      if (mounted.current) setError(describeImpactError(err));
    } finally {
      inFlight.current = false;
      if (mounted.current) setRunning(false);
    }
  };

  return (
    <section aria-labelledby="hub-dryrun-title" className="mt-8 border-t border-border pt-6" data-testid="hub-dryrun">
      <h3 id="hub-dryrun-title" className="flex items-center gap-2 text-sm font-semibold text-slate-800"><ShieldAlert className="h-4 w-4" aria-hidden="true" />Etki Önizlemesi</h3>
      <p className="text-xs text-muted-foreground">Bir sahiplik değişikliğinin etkisini önceden görün. Bu işlem hiçbir ayarı değiştirmez ve kaydetme/uygulama seçeneği sunmaz.</p>
      {!ownership && (
        <p className="mt-3 rounded-xl border border-dashed border-border bg-muted/30 px-3 py-3 text-sm text-muted-foreground" data-testid="hub-dryrun-unknown-current" role="status">
          Mevcut sahiplik görünümü okunamadı. Yine de bir seçim yaparak analiz isteyebilirsiniz; sonuç, yapılandırmanızın durumunu da bildirir.
        </p>
      )}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {EDITABLE_FIELDS.map((def) => {
          const cur = currentValue(ownership, def);
          const value = Object.prototype.hasOwnProperty.call(selections, def.field) ? selections[def.field] : cur;
          const isAuthority = def.kind === "authority";
          const id = `hub-sel-${def.field}`;
          return (
            <div key={def.field}>
              <label htmlFor={id} className="block text-xs font-medium text-slate-700">{FIELD_LABELS[def.field]}</label>
              <NativeSelect id={id} value={value} disabled={running} onChange={(v) => onSelect(def.field, v)}
                options={isAuthority ? AUTHORITY_OPTIONS : PROVIDER_OPTIONS} labels={isAuthority ? AUTHORITY_LABELS : PROVIDER_LABELS}
                emptyLabel={isAuthority ? (cur === UNKNOWN_CURRENT ? "Mevcut değer bilinmiyor" : null) : "Seçilmedi"} emptyDisabled={isAuthority} />
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">Tahsilat/ödeme sahibi kasa/banka sahibinden türetilir ve ayrıca seçilemez.</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button type="button" onClick={analyze} disabled={!canRun} aria-busy={running} className="h-10 rounded-xl" data-testid="hub-analyze">
          {running ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />Analiz ediliyor…</> : "Etkiyi Analiz Et"}
        </Button>
        {!hasChange && !running && <span className="text-xs text-muted-foreground">Analiz için önce bir seçimi değiştirin.</span>}
      </div>
      <div aria-live="polite">
        {error && <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert" data-testid="hub-dryrun-error">{error}</p>}
      </div>
      {result && <ImpactResult result={result} />}
    </section>
  );
}

export default function IntegrationHubPanel() {
  const [state, setState] = useState({ status: "loading" });       // loading | hidden | ready | unavailable | rate_limited
  const [showSkeleton, setShowSkeleton] = useState(false);
  const alive = useRef(true);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const data = await api.get(HUB_PATH, HUB_GET_CONFIG);
      const hub = normalizeHub(data);
      if (!alive.current) return;
      setState(hub ? { status: "ready", hub } : { status: "unavailable" });
    } catch (err) {
      if (!alive.current) return;
      const c = classifyHubError(err);
      setState(c.kind === "hidden" ? { status: "hidden" } : c.kind === "rate_limited" ? { status: "rate_limited", retryAfter: c.retryAfter } : { status: "unavailable" });
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
  }, [load]);

  useEffect(() => {
    if (state.status !== "loading") { setShowSkeleton(false); return undefined; }
    const t = setTimeout(() => setShowSkeleton(true), SKELETON_DELAY_MS);
    return () => clearTimeout(t);
  }, [state.status]);

  if (state.status === "hidden") return null;
  if (state.status === "loading") {
    return showSkeleton ? (
      <Card className="surface-card-soft border-border shadow-sm" data-testid="integration-hub-loading" aria-busy="true">
        <CardContent className="py-8 text-center text-muted-foreground" role="status"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" aria-hidden="true" />Entegrasyon Merkezi yükleniyor…</CardContent>
      </Card>
    ) : null;
  }
  if (state.status === "unavailable" || state.status === "rate_limited") {
    return (
      <Card className="surface-card-soft border-border shadow-sm" data-testid="integration-hub-error">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 py-5">
          <p className="text-sm text-slate-700" role="status">
            {state.status === "rate_limited"
              ? `Çok fazla istek gönderildi. ${state.retryAfter ? `Lütfen ${state.retryAfter} saniye sonra` : "Lütfen biraz bekleyip"} tekrar deneyin.`
              : "Entegrasyon Merkezi şu anda yüklenemedi. Firmalarım ekranınız etkilenmedi."}
          </p>
          <Button type="button" variant="outline" onClick={load} className="h-9 rounded-xl"><RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />Tekrar dene</Button>
        </CardContent>
      </Card>
    );
  }

  const { hub } = state;
  return (
    <Card className="surface-card-soft border-border shadow-sm" data-testid="integration-hub" role="region" aria-labelledby="hub-title">
      <CardHeader className="pb-3">
        <CardTitle id="hub-title" className="text-base">Entegrasyon Merkezi</CardTitle>
        <CardDescription>Bağlı sağlayıcılarınızın durumu ve verilerin hangi sistemde yönetildiği. Bu bölüm salt-okunurdur.</CardDescription>
      </CardHeader>
      <CardContent>
        <CodeList codes={hub.issues} testid="hub-global-issues" />
        {hub.providers.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border bg-muted/30 px-3 py-4 text-sm text-muted-foreground" data-testid="hub-empty">Gösterilecek sağlayıcı bulunamadı.</p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="hub-cards">
            {hub.providers.map((p, i) => <ProviderCard key={`${p.provider}-${i}`} p={p} />)}
          </ul>
        )}
        <OwnershipMatrix ownership={hub.ownership} />
        <DryRunSection ownership={hub.ownership} />
      </CardContent>
    </Card>
  );
}
